import React, { useState, useEffect } from 'react';
import { VisitModel, ProspectModel, PatientModel } from '../../models';
import { ReportList, ColorPieChartComponent } from '../../components';
import { ChartData } from '../../interfaces';
import './reports.scss';

const ViewReports = () => {
  const [visitsPieData, setVisitsPieData] = useState<ChartData[]>([]);
  const [paidPieData, setPaidPieData] = useState<ChartData[]>([]);
  const [totalBarData, setTotalBarData] = useState<number>(0);
  const [barData, setBarData] = useState<any[]>([]);

  const getVisitRecords = async () => {
    const resp:any = await VisitModel.fetchAll();
  
    const getCouponName = (url:string) => {
      try {
        if (!url) return "Unknown";
        const urlObj = new URL(url);
        const params = new URLSearchParams(urlObj.search);
        const utmContent = params.get("utm_content");
  
        if (utmContent) return `${utmContent} (Email Campaign)`;
        if (url.toLowerCase().includes("getfit25")) {
          return url.toLowerCase().includes("home-v2") ? "GetFit25 (Home-v2)": "GetFit25";
        }
        if (url.toLowerCase().includes("healthy2025"))  {
          return url.toLowerCase().includes("home-v2") ? "Healthy2025 (Home-v2)": "Healthy2025";
        };
        return url;
      } catch {
        return url;
      }
    };
  
    const transformedData = Object.values(
      resp.data.reduce((acc:any, item:any) => {
        const key = getCouponName(item.originUrl);
        if (!acc[key]) {
          acc[key] = { name: key, value: 0 };
        }
        acc[key].value += 1;
        return acc;
      }, {})
    ) as ChartData[];
  
    setVisitsPieData(transformedData);
  };
  

  const getProspectRecords = async() => {
    const resp:any = await ProspectModel.fetchAll();
    let total = 0;

    const groupedData = Object.entries(
      resp.data.reduce((acc: any, item: any) => {
        if (item.stepsSeen) {
          acc[item.stepsSeen] = (acc[item.stepsSeen] || 0) + 1;
          total += 1;
        }
        return acc;
      }, {})
    )
      .map(([step, stops]) => ({ name: `Step ${step}`, value: stops }))
      .sort((a, b) => {
        const stepA = parseInt(a.name.replace("Step ", ""), 10);
        const stepB = parseInt(b.name.replace("Step ", ""), 10);
        return stepA - stepB;
      });
    
    setBarData(groupedData);
    setTotalBarData(total);
  };

  const getPatientBuyerRecords = async() => {
    const resp:any = await PatientModel.fetchAll({ paid: true});

    const origins = {
      'https://bodymiracle.com/get-started-tirzepatide-vip-v2/': 'Test',
    } as any;

    const transformedData = Object.values(
      resp.data.reduce((acc:ChartData[], item:{[key:string]:any}) => {
        const key:any = origins[item.originUrl] || item.originUrl || 'https://bodymiracle.com/';

        if (!acc[key]) {
          acc[key] = { name: key, value: 0 };
        }
        acc[key].value += 1;
        return acc;
      }, {})) as ChartData[];

    setPaidPieData(transformedData);
  };
  
  useEffect(()=> {
    getVisitRecords();
    getProspectRecords();
    getPatientBuyerRecords();
  }, []);

  const totalVisits = visitsPieData.reduce((sum, item) => sum + item.value, 0);
  const totalPayments = paidPieData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="reports-dashboard">
      <div className="reports-header">
        <h1>Reports Dashboard</h1>
        <div className="reports-summary">
          <div className="summary-stat">
            <span className="stat-value">{totalVisits.toLocaleString()}</span>
            <span className="stat-label">Total Visits</span>
          </div>
          <div className="summary-stat">
            <span className="stat-value">{totalBarData.toLocaleString()}</span>
            <span className="stat-label">Form Entries</span>
          </div>
          <div className="summary-stat">
            <span className="stat-value">{totalPayments.toLocaleString()}</span>
            <span className="stat-label">Payments</span>
          </div>
        </div>
      </div>

      <div className="reports-grid">
        <div className="report-card report-card-wide">
          <div className="report-card-header">
            <h2>URL Visits</h2>
          </div>
          <div className="report-card-content">
            <div className="chart-container">
              <ColorPieChartComponent data={visitsPieData} />
            </div>
            <ReportList data={visitsPieData} label="visits" header="" colorCoded/>
          </div>
        </div>

        <div className="report-card report-card-wide">
          <div className="report-card-header">
            <h2>Form Drop-off Analysis</h2>
            <span className="report-subtitle">{totalBarData} total entries</span>
          </div>
          <div className="report-card-content">
            <div className="chart-container">
              <ColorPieChartComponent data={barData} />
            </div>
            <ReportList data={barData} label="users" header="" colorCoded />
          </div>
        </div>

        <div className="report-card report-card-wide">
          <div className="report-card-header">
            <h2>Payment Origins</h2>
          </div>
          <div className="report-card-content">
            <div className="chart-container">
              <ColorPieChartComponent data={paidPieData} />
            </div>
            <ReportList data={paidPieData} label="payments" header="" colorCoded/>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewReports;
